/**
 * ALTIORA · Admin Knowledge Engine — cerveau commun de la plateforme.
 *
 * Interface d'administration du moteur de connaissances :
 *  - Statistiques globales (nombre d'items, arêtes, distribution par source)
 *  - Liste filtrable (source_type, facette, texte, statut) + pagination
 *  - Création / édition / suppression d'items
 *  - Édition des relations typées (graphe)
 *  - Import CSV massif
 *  - Bouton « Régénérer patrimoine seed »
 *  - Preview du raisonnement via `POST /reason` (test interactif)
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Brain, Search, Plus, Upload, RefreshCw, Trash2, Save, Link2, X,
  ChevronRight, Info, Play, GitBranch, TestTube, Sparkles,
} from "lucide-react";
import {
  PageShell, PageHeader, Card, Section, PrimaryButton, GhostButton,
  FormField, Input, Alert, Loader,
  NAVY, CHAMPAGNE, IVORY, SLATE, NAVY_DARK as INK,
} from "@/components/altiora/PremiumUI";
import { TopBar } from "@/components/layout/TopBar";
import { api } from "@/lib/api";

const RELATION_LABELS = {
  depends_on: "dépend de", requires: "nécessite", recommends: "recommande",
  complements: "complète", replaces: "remplace", related_to: "est lié à",
  compliant_with: "conforme à", forbidden_by: "interdit par",
  associated_product: "produit", associated_profession: "profession",
  associated_skill: "compétence", associated_regulation: "réglementation",
  associated_organization: "organisme", associated_partner: "partenaire",
  associated_territory: "territoire", associated_opportunity: "opportunité",
  associated_risk: "risque", associated_decision: "décision",
  associated_event: "événement", associated_period: "période",
  associated_experience: "expérience", triggers: "déclenche",
};

const SOURCE_LABELS = {
  regulation_official: "Réglementaire", institution_doc: "Institution",
  partner_doc: "Partenaire", professional_literature: "Littérature pro",
  internal_procedure: "Procédure interne", best_practice: "Bonne pratique",
  field_experience: "Retour d'expérience", field_observation: "Observation terrain",
  jurisprudence: "Jurisprudence", altiora_original: "ALTIORA",
};

export default function AdminKnowledgeEngine() {
  const [stats, setStats] = useState(null);
  const [schema, setSchema] = useState(null);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [filterFacetKey, setFilterFacetKey] = useState("");
  const [filterFacetValue, setFilterFacetValue] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [subgraph, setSubgraph] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showReason, setShowReason] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const loadStats = async () => {
    try {
      const [s, f] = await Promise.all([
        api.get("/knowledge-engine/stats"),
        api.get("/knowledge-engine/facets"),
      ]);
      setStats(s.data);
      setSchema(f.data);
    } catch { toast.error("Impossible de charger les statistiques"); }
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "50");
      if (q) params.set("q", q);
      if (filterSource) params.set("source_type", filterSource);
      if (filterFacetKey && filterFacetValue) {
        params.set("facet_key", filterFacetKey);
        params.set("facet_value", filterFacetValue);
      }
      const { data } = await api.get(`/knowledge-engine/items?${params}`);
      setItems(data.items || []);
      setCount(data.count || 0);
    } catch { toast.error("Impossible de charger les items"); }
    finally { setLoading(false); }
  };

  const loadItem = async (id) => {
    try {
      const { data } = await api.get(`/knowledge-engine/items/${id}`);
      setSelected(data);
      const g = await api.get(`/knowledge-engine/items/${id}/graph?depth=1`);
      setSubgraph(g.data);
    } catch { toast.error("Item introuvable"); }
  };

  useEffect(() => { loadStats(); loadItems(); }, []);
  useEffect(() => { if (selectedId) loadItem(selectedId); }, [selectedId]);

  const runSeed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/knowledge-engine/seed");
      toast.success(`Seed terminé — ${data.items_seeded} items · ${data.edges_seeded} arêtes`);
      loadStats(); loadItems();
    } catch { toast.error("Le seed a échoué"); }
    finally { setSeeding(false); }
  };

  const deleteItem = async (id) => {
    if (!confirm("Supprimer définitivement cet item ?")) return;
    try {
      await api.delete(`/knowledge-engine/items/${id}`);
      toast.success("Item supprimé");
      if (selectedId === id) { setSelectedId(null); setSelected(null); }
      loadItems(); loadStats();
    } catch { toast.error("Échec de la suppression"); }
  };

  return (
    <PageShell>
      <TopBar />
      <div className="max-w-[1400px] mx-auto px-6 py-8" data-testid="admin-ke-root">
        <PageHeader
          eyebrow="ALTIORA · Administration"
          title="Knowledge Engine — cerveau commun"
          subtitle="Gérez les connaissances et le graphe qui alimentent tous les modules d'ALTIORA."
          testid="admin-ke-header"
        />

        {/* STATS */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6" data-testid="admin-ke-stats">
            <StatTile label="Connaissances" value={stats.total_items} icon={Brain} />
            <StatTile label="Arêtes du graphe" value={stats.total_edges} icon={GitBranch} />
            <StatTile label="Sources officielles"
                       value={stats.by_source?.regulation_official || 0} icon={Sparkles} />
            <StatTile label="Types de source"
                       value={Object.keys(stats.by_source || {}).length} icon={Info} />
          </div>
        )}

        {/* ACTIONS PRIMAIRES */}
        <div className="flex flex-wrap gap-3 mt-6">
          <PrimaryButton onClick={() => setShowCreate(true)} testid="admin-ke-btn-create">
            <Plus size={13} /> Ajouter une connaissance
          </PrimaryButton>
          <GhostButton onClick={() => setShowImport(true)} testid="admin-ke-btn-import">
            <Upload size={13} /> Importer CSV
          </GhostButton>
          <GhostButton onClick={() => setShowReason(true)} testid="admin-ke-btn-reason">
            <TestTube size={13} /> Tester le raisonnement
          </GhostButton>
          <GhostButton onClick={runSeed} disabled={seeding} testid="admin-ke-btn-seed">
            <RefreshCw size={13} /> {seeding ? "Seed en cours…" : "Régénérer patrimoine"}
          </GhostButton>
        </div>

        {/* FILTRES */}
        <Card className="p-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <FormField label="Recherche" testid="admin-ke-ff-q">
              {(a) => <Input testid="admin-ke-search" placeholder="Titre, résumé, tag…"
                             value={q} onChange={(e) => setQ(e.target.value)} {...a} />}
            </FormField>
            <FormField label="Type de source" testid="admin-ke-ff-src">
              {() => (
                <select data-testid="admin-ke-filter-source"
                        className="w-full border rounded px-2 py-1 text-sm"
                        value={filterSource}
                        onChange={(e) => setFilterSource(e.target.value)}>
                  <option value="">Tous</option>
                  {schema?.source_types?.map((t) => (
                    <option key={t} value={t}>{SOURCE_LABELS[t] || t}</option>
                  ))}
                </select>
              )}
            </FormField>
            <FormField label="Facette (clé)" testid="admin-ke-ff-fk">
              {() => (
                <select data-testid="admin-ke-filter-fkey"
                        className="w-full border rounded px-2 py-1 text-sm"
                        value={filterFacetKey}
                        onChange={(e) => setFilterFacetKey(e.target.value)}>
                  <option value="">—</option>
                  {schema?.facet_keys?.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              )}
            </FormField>
            <FormField label="Facette (valeur)" testid="admin-ke-ff-fv">
              {(a) => <Input testid="admin-ke-filter-fval" placeholder="ex : reer, CA-QC"
                             value={filterFacetValue}
                             onChange={(e) => setFilterFacetValue(e.target.value)} {...a} />}
            </FormField>
          </div>
          <div className="mt-3 flex gap-2">
            <PrimaryButton onClick={loadItems} testid="admin-ke-apply-filters">
              <Search size={13} /> Filtrer
            </PrimaryButton>
            <GhostButton onClick={() => {
              setQ(""); setFilterSource(""); setFilterFacetKey(""); setFilterFacetValue("");
              setTimeout(loadItems, 50);
            }} testid="admin-ke-reset-filters">
              Réinitialiser
            </GhostButton>
          </div>
        </Card>

        {/* LIST + DETAIL */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mt-4">
          <div className="lg:col-span-2">
            <Card className="p-0">
              <div className="p-4 border-b" style={{ borderColor: "#e5e0d0" }}>
                <div className="text-[11px] uppercase tracking-widest font-mono"
                     style={{ color: SLATE }}>
                  {count} résultat{count > 1 ? "s" : ""}
                </div>
              </div>
              {loading ? (
                <div className="p-8 flex justify-center"><Loader size={20} /></div>
              ) : (
                <ul className="max-h-[600px] overflow-y-auto" data-testid="admin-ke-list">
                  {items.map((it) => (
                    <li key={it.id}
                        onClick={() => setSelectedId(it.id)}
                        data-testid={`admin-ke-item-${it.id}`}
                        className={`p-3 border-b cursor-pointer transition ${
                          selectedId === it.id ? "bg-amber-50" : "hover:bg-gray-50"
                        }`}
                        style={{ borderColor: "#e5e0d0" }}>
                      <div className="text-sm font-medium" style={{ color: NAVY }}>
                        {it.title}
                      </div>
                      <div className="text-[11px] mt-1 flex flex-wrap gap-1"
                           style={{ color: SLATE }}>
                        <span className="px-1.5 py-0.5 rounded text-[10px]"
                              style={{ background: "#f3ecd7", color: NAVY }}>
                          {SOURCE_LABELS[it.source_type] || it.source_type}
                        </span>
                        <span>Confiance {it.confidence}</span>
                        {it.relations?.length ? (
                          <span>· {it.relations.length} rel.</span>
                        ) : null}
                      </div>
                    </li>
                  ))}
                  {!items.length && !loading && (
                    <li className="p-8 text-center text-sm" style={{ color: SLATE }}>
                      Aucun item ne correspond aux filtres.
                    </li>
                  )}
                </ul>
              )}
            </Card>
          </div>

          <div className="lg:col-span-3">
            {selected ? (
              <ItemDetailPanel
                item={selected} subgraph={subgraph}
                schema={schema}
                onDelete={() => deleteItem(selected.id)}
                onRefresh={() => loadItem(selected.id)}
                onDeactivate={async () => {
                  await api.post(`/knowledge-engine/items/${selected.id}/deactivate`);
                  toast.success("Item désactivé"); loadItem(selected.id); loadItems();
                }}
              />
            ) : (
              <Card className="p-8 text-center" data-testid="admin-ke-empty-detail">
                <Brain size={32} className="mx-auto mb-3" style={{ color: CHAMPAGNE }} />
                <div className="text-sm" style={{ color: NAVY }}>
                  Sélectionnez une connaissance dans la liste pour voir son détail et son graphe.
                </div>
              </Card>
            )}
          </div>
        </div>

        {showCreate && (
          <CreateItemModal
            schema={schema}
            onClose={() => setShowCreate(false)}
            onCreated={() => { setShowCreate(false); loadItems(); loadStats(); }}
          />
        )}
        {showImport && (
          <ImportCsvModal
            onClose={() => setShowImport(false)}
            onImported={() => { setShowImport(false); loadItems(); loadStats(); }}
          />
        )}
        {showReason && (
          <ReasonTesterModal
            onClose={() => setShowReason(false)}
            schema={schema}
          />
        )}
      </div>
    </PageShell>
  );
}

/* ── STAT TILE ─────────────────────────────────────────────────────── */
function StatTile({ label, value, icon: Icon }) {
  return (
    <Card className="p-4" data-testid={`admin-ke-stat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center"
             style={{ background: "#f3ecd7" }}>
          <Icon size={18} style={{ color: NAVY }} />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest font-mono" style={{ color: SLATE }}>
            {label}
          </div>
          <div className="text-2xl font-serif" style={{ color: NAVY }}>
            {typeof value === "number" ? value.toLocaleString("fr-CA") : value}
          </div>
        </div>
      </div>
    </Card>
  );
}

/* ── ITEM DETAIL ───────────────────────────────────────────────────── */
function ItemDetailPanel({ item, subgraph, schema, onDelete, onRefresh, onDeactivate }) {
  const [showAddRel, setShowAddRel] = useState(false);
  return (
    <Card className="p-6" data-testid="admin-ke-detail">
      <div className="flex justify-between items-start">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-mono"
               style={{ color: CHAMPAGNE }}>
            {SOURCE_LABELS[item.source_type] || item.source_type} · confiance {item.confidence}
          </div>
          <h2 className="text-xl font-serif mt-1" style={{ color: NAVY }}>
            {item.title}
          </h2>
          <p className="text-sm mt-2" style={{ color: INK }}>{item.summary}</p>
        </div>
        <div className="flex gap-2">
          <GhostButton onClick={onDeactivate} testid="admin-ke-btn-deactivate">
            Désactiver
          </GhostButton>
          <GhostButton onClick={onDelete} testid="admin-ke-btn-delete">
            <Trash2 size={13} /> Supprimer
          </GhostButton>
        </div>
      </div>

      <Section title="Facettes" className="mt-4">
        <div className="flex flex-wrap gap-2 mt-2">
          {Object.entries(item.facets || {}).map(([k, vs]) => (vs || []).map((v) => (
            <span key={`${k}:${v}`}
                   className="px-2 py-0.5 rounded text-[11px]"
                   style={{ background: "#f7f2e5", color: NAVY, border: "1px solid #e5d9b8" }}>
              <strong>{k}</strong>: {v}
            </span>
          )))}
        </div>
      </Section>

      <Section title={`Relations (${(item.relations || []).length})`} className="mt-4">
        <div className="flex justify-end mb-2">
          <GhostButton onClick={() => setShowAddRel(true)} testid="admin-ke-btn-add-rel">
            <Link2 size={13} /> Ajouter une relation
          </GhostButton>
        </div>
        <ul className="space-y-1" data-testid="admin-ke-relations">
          {(item.relations || []).map((r, idx) => (
            <li key={idx} className="text-xs flex items-center gap-2 py-1 border-b"
                style={{ borderColor: "#e5e0d0" }}>
              <span className="px-1.5 py-0.5 rounded text-[10px] font-mono"
                    style={{ background: "#f3ecd7", color: NAVY }}>
                {RELATION_LABELS[r.type] || r.type}
              </span>
              <span style={{ color: INK }}>{r.target_id}</span>
              {r.notes && <span style={{ color: SLATE }}>· {r.notes}</span>}
              <button className="ml-auto text-red-500 hover:underline"
                       data-testid={`admin-ke-rm-rel-${idx}`}
                       onClick={async () => {
                         await api.delete(
                           `/knowledge-engine/items/${item.id}/relations/${r.target_id}/${r.type}`
                         );
                         toast.success("Relation supprimée"); onRefresh();
                       }}>
                <X size={12} />
              </button>
            </li>
          ))}
          {!(item.relations || []).length && (
            <li className="text-xs" style={{ color: SLATE }}>Aucune relation typée.</li>
          )}
        </ul>
      </Section>

      {subgraph && subgraph.nodes?.length > 1 && (
        <Section title="Sous-graphe (profondeur 1)" className="mt-4">
          <div className="text-xs mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
            {Array.from(new Map(
              subgraph.nodes.filter((n) => n.id !== item.id).map((n) => [n.id, n])
            ).values()).map((n) => (
              <div key={n.id} className="p-2 rounded border text-xs"
                   style={{ background: "#faf6ea", borderColor: "#e5d9b8", color: NAVY }}
                   data-testid={`admin-ke-graph-node-${n.id}`}>
                <div className="font-medium truncate">{n.title}</div>
                <div className="text-[10px] uppercase tracking-widest font-mono"
                     style={{ color: SLATE }}>
                  {SOURCE_LABELS[n.source_type] || n.source_type}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title="Contenu (markdown)" className="mt-4">
        <pre className="text-xs whitespace-pre-wrap p-3 rounded"
             style={{ background: "#fbf6ea", color: INK, border: "1px solid #e5d9b8" }}>
{item.body_md || "—"}
        </pre>
      </Section>

      {showAddRel && (
        <AddRelationModal
          sourceId={item.id}
          schema={schema}
          onClose={() => setShowAddRel(false)}
          onAdded={() => { setShowAddRel(false); onRefresh(); }}
        />
      )}
    </Card>
  );
}

/* ── CREATE MODAL ──────────────────────────────────────────────────── */
function CreateItemModal({ schema, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", summary: "", body_md: "",
    source_type: "best_practice", confidence: 70,
    facets: { domain: "", product: "", situation: "", territory: "",
               profession: "", regulation: "" },
    tags: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));
  const setFacet = (k) => (e) => setForm((f) => ({
    ...f, facets: { ...f.facets, [k]: e.target.value },
  }));
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Titre requis"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title, summary: form.summary, body_md: form.body_md,
        source_type: form.source_type, confidence: parseInt(form.confidence) || 70,
        facets: Object.fromEntries(Object.entries(form.facets).map(
          ([k, v]) => [k, (v || "").split("|").map((x) => x.trim()).filter(Boolean)]
        )),
        tags: (form.tags || "").split(",").map((x) => x.trim()).filter(Boolean),
      };
      await api.post("/knowledge-engine/items", payload);
      toast.success("Item créé");
      onCreated();
    } catch (err) { toast.error("Création échouée"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Ajouter une connaissance" onClose={onClose} testid="admin-ke-create-modal">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FormField label="Titre" required testid="admin-ke-create-ff-title">
          {(a) => <Input testid="admin-ke-create-title" value={form.title}
                         onChange={set("title")} {...a} />}
        </FormField>
        <FormField label="Type de source" required testid="admin-ke-create-ff-src">
          {() => (
            <select data-testid="admin-ke-create-source"
                    className="w-full border rounded px-2 py-1 text-sm"
                    value={form.source_type} onChange={set("source_type")}>
              {schema?.source_types?.map((t) => (
                <option key={t} value={t}>{SOURCE_LABELS[t] || t}</option>
              ))}
            </select>
          )}
        </FormField>
        <FormField label="Résumé" testid="admin-ke-create-ff-summary" className="md:col-span-2">
          {(a) => <Input testid="admin-ke-create-summary" value={form.summary}
                         onChange={set("summary")} {...a} />}
        </FormField>
        <FormField label="Contenu (markdown)" testid="admin-ke-create-ff-body"
                    className="md:col-span-2">
          {() => (
            <textarea data-testid="admin-ke-create-body" rows={5}
                       className="w-full border rounded px-2 py-1 text-sm"
                       value={form.body_md} onChange={set("body_md")} />
          )}
        </FormField>
        <FormField label="Confiance (0-100)" testid="admin-ke-create-ff-conf">
          {(a) => <Input testid="admin-ke-create-conf" type="number" min="0" max="100"
                         value={form.confidence} onChange={set("confidence")} {...a} />}
        </FormField>
        <FormField label="Tags (séparés par virgules)" testid="admin-ke-create-ff-tags">
          {(a) => <Input testid="admin-ke-create-tags" value={form.tags}
                         onChange={set("tags")} {...a} />}
        </FormField>
        {["domain", "product", "situation", "territory", "profession", "regulation"].map((fk) => (
          <FormField key={fk} label={`Facette · ${fk} (séparés par |)`}
                      testid={`admin-ke-create-ff-facet-${fk}`}>
            {(a) => <Input testid={`admin-ke-create-facet-${fk}`}
                            value={form.facets[fk]}
                            onChange={setFacet(fk)} {...a} />}
          </FormField>
        ))}
      </div>
      <div className="mt-4 flex gap-2 justify-end">
        <GhostButton onClick={onClose} testid="admin-ke-create-cancel">Annuler</GhostButton>
        <PrimaryButton onClick={submit} disabled={saving} testid="admin-ke-create-submit">
          <Save size={13} /> {saving ? "Enregistrement…" : "Enregistrer"}
        </PrimaryButton>
      </div>
    </Modal>
  );
}

/* ── ADD RELATION MODAL ────────────────────────────────────────────── */
function AddRelationModal({ sourceId, schema, onClose, onAdded }) {
  const [targetId, setTargetId] = useState("");
  const [type, setType] = useState("recommends");
  const [weight, setWeight] = useState(1.0);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!targetId.trim()) { toast.error("ID cible requis"); return; }
    setSaving(true);
    try {
      await api.post(`/knowledge-engine/items/${sourceId}/relations`, {
        target_id: targetId, type, weight: parseFloat(weight) || 1.0, notes,
      });
      toast.success("Relation ajoutée"); onAdded();
    } catch { toast.error("Impossible d'ajouter (item cible introuvable ?)"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Ajouter une relation typée" onClose={onClose} testid="admin-ke-add-rel-modal">
      <FormField label="ID de l'item cible" required testid="admin-ke-rel-ff-target">
        {(a) => <Input testid="admin-ke-rel-target" placeholder="kb_..."
                       value={targetId} onChange={(e) => setTargetId(e.target.value)} {...a} />}
      </FormField>
      <FormField label="Type de relation" required testid="admin-ke-rel-ff-type">
        {() => (
          <select data-testid="admin-ke-rel-type"
                  className="w-full border rounded px-2 py-1 text-sm"
                  value={type} onChange={(e) => setType(e.target.value)}>
            {schema?.relation_types?.map((t) => (
              <option key={t} value={t}>{RELATION_LABELS[t] || t}</option>
            ))}
          </select>
        )}
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Poids" testid="admin-ke-rel-ff-weight">
          {(a) => <Input testid="admin-ke-rel-weight" type="number" step="0.1" min="0" max="1"
                         value={weight} onChange={(e) => setWeight(e.target.value)} {...a} />}
        </FormField>
        <FormField label="Notes" testid="admin-ke-rel-ff-notes">
          {(a) => <Input testid="admin-ke-rel-notes"
                         value={notes} onChange={(e) => setNotes(e.target.value)} {...a} />}
        </FormField>
      </div>
      <div className="mt-4 flex gap-2 justify-end">
        <GhostButton onClick={onClose} testid="admin-ke-rel-cancel">Annuler</GhostButton>
        <PrimaryButton onClick={submit} disabled={saving} testid="admin-ke-rel-submit">
          <Link2 size={13} /> Ajouter
        </PrimaryButton>
      </div>
    </Modal>
  );
}

/* ── IMPORT CSV MODAL ─────────────────────────────────────────────── */
function ImportCsvModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/knowledge-engine/import/csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`${data.ok} item(s) importé(s)`);
      if (data.ok > 0) onImported();
    } catch { toast.error("Import échoué"); }
    finally { setUploading(false); }
  };

  return (
    <Modal title="Import CSV massif" onClose={onClose} testid="admin-ke-import-modal">
      <Alert variant="info" title="Format attendu" testid="admin-ke-import-alert">
        Colonnes : <code>title, source_type</code> (requis), plus optionnels :{" "}
        <code>summary, body_md, confidence, status, citation_label, citation_url,
        f_domain, f_product, f_territory, f_profession, f_regulation, f_situation,
        tags, expires_at</code>. Les listes utilisent <code>|</code> comme séparateur.
      </Alert>
      <div className="mt-3">
        <input type="file" accept=".csv,text/csv"
               data-testid="admin-ke-import-file"
               onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </div>
      {result && (
        <Alert variant={result.errors?.length ? "warning" : "success"}
                title={`Import terminé — ${result.ok} lignes réussies`}
                testid="admin-ke-import-result">
          {result.errors?.length ? (
            <ul className="text-xs mt-2">
              {result.errors.slice(0, 10).map((er, i) => (
                <li key={i}>Ligne {er.row}: {er.error}</li>
              ))}
            </ul>
          ) : "Aucune erreur."}
        </Alert>
      )}
      <div className="mt-4 flex gap-2 justify-end">
        <GhostButton onClick={onClose} testid="admin-ke-import-close">Fermer</GhostButton>
        <PrimaryButton onClick={submit} disabled={!file || uploading}
                        testid="admin-ke-import-submit">
          <Upload size={13} /> {uploading ? "Import…" : "Importer"}
        </PrimaryButton>
      </div>
    </Modal>
  );
}

/* ── REASON TESTER ─────────────────────────────────────────────────── */
function ReasonTesterModal({ onClose, schema }) {
  const [text, setText] = useState("");
  const [profession, setProfession] = useState("conseiller_csf");
  const [territory, setTerritory] = useState("CA-QC");
  const [situation, setSituation] = useState("");
  const [period, setPeriod] = useState("");
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  const submit = async () => {
    setRunning(true);
    try {
      const body = {
        text: text || null,
        profile: { profession, territory },
        client: situation ? { situation } : null,
        temporal: period ? { period } : null,
        with_graph: true, graph_depth: 1, limit: 8,
      };
      const { data } = await api.post("/knowledge-engine/reason", body);
      setResult(data);
    } catch { toast.error("Erreur de raisonnement"); }
    finally { setRunning(false); }
  };

  return (
    <Modal title="Tester le raisonnement contextuel" onClose={onClose}
            testid="admin-ke-reason-modal" wide>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Recherche textuelle" testid="admin-ke-reason-ff-text">
          {(a) => <Input testid="admin-ke-reason-text" value={text}
                         onChange={(e) => setText(e.target.value)} {...a} />}
        </FormField>
        <FormField label="Profession" testid="admin-ke-reason-ff-prof">
          {(a) => <Input testid="admin-ke-reason-prof" value={profession}
                         onChange={(e) => setProfession(e.target.value)} {...a} />}
        </FormField>
        <FormField label="Territoire" testid="admin-ke-reason-ff-terr">
          {(a) => <Input testid="admin-ke-reason-terr" value={territory}
                         onChange={(e) => setTerritory(e.target.value)} {...a} />}
        </FormField>
        <FormField label="Situation client" testid="admin-ke-reason-ff-sit">
          {(a) => <Input testid="admin-ke-reason-sit" placeholder="retraite_proche, achat_premier_bien…"
                         value={situation} onChange={(e) => setSituation(e.target.value)} {...a} />}
        </FormField>
        <FormField label="Période" testid="admin-ke-reason-ff-per">
          {(a) => <Input testid="admin-ke-reason-per" placeholder="reer_deadline, celi_dotation…"
                         value={period} onChange={(e) => setPeriod(e.target.value)} {...a} />}
        </FormField>
      </div>
      <div className="mt-3 flex gap-2">
        <PrimaryButton onClick={submit} disabled={running} testid="admin-ke-reason-run">
          <Play size={13} /> {running ? "Analyse…" : "Lancer le raisonnement"}
        </PrimaryButton>
      </div>
      {result && (
        <div className="mt-4" data-testid="admin-ke-reason-result">
          <div className="text-[11px] font-mono uppercase tracking-widest mb-2"
               style={{ color: SLATE }}>
            Confiance globale : {result.confidence} · {result.items?.length} items retenus
          </div>
          <ul className="space-y-2 max-h-[400px] overflow-y-auto">
            {result.items?.map((i, idx) => (
              <li key={i.id} className="p-2 rounded border text-xs"
                  style={{ background: "#fbf6ea", borderColor: "#e5d9b8" }}>
                <div className="font-medium" style={{ color: NAVY }}>
                  #{idx + 1} · {i.title} <span style={{ color: SLATE }}>· score {i.score}</span>
                </div>
                <div className="text-[10px] mt-1" style={{ color: SLATE }}>
                  {SOURCE_LABELS[i.source_type] || i.source_type} · confiance {i.confidence}
                </div>
                {i.summary && <div className="mt-1" style={{ color: INK }}>{i.summary}</div>}
              </li>
            ))}
          </ul>
          <details className="mt-3 text-xs">
            <summary className="cursor-pointer" style={{ color: CHAMPAGNE }}>
              Voir la trace de raisonnement
            </summary>
            <ol className="mt-2 space-y-1" style={{ color: INK }}>
              {result.reasoning_steps?.map((s, i) => (
                <li key={i}>{i + 1}. {s}</li>
              ))}
            </ol>
          </details>
        </div>
      )}
    </Modal>
  );
}

/* ── MODAL HELPER ──────────────────────────────────────────────────── */
function Modal({ title, children, onClose, testid, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
         data-testid={testid} onClick={onClose}>
      <div className={`bg-white rounded-lg shadow-2xl w-full ${wide ? "max-w-4xl" : "max-w-2xl"} max-h-[90vh] overflow-y-auto`}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center p-4 border-b"
             style={{ borderColor: "#e5e0d0" }}>
          <h3 className="font-serif text-lg" style={{ color: NAVY }}>{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"
                  data-testid={`${testid}-close`}>
            <X size={16} />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
